import { verifyPasskeyApproval } from './approval.js';
import { approvalChallengeFor } from './approval-batch.js';
import { DOMAIN, hashCanonical } from './hash.js';
import { exportP256PublicJwk, importP256PublicJwk, isEcPublicJwk, jwkThumbprint, } from './keys.js';
import { isWebAuthnAssertion } from './webauthn.js';
export const DEFAULT_ENDORSEMENT_TTL_MS = 90 * 24 * 60 * 60 * 1000;
export const DEFAULT_CLOCK_SKEW_MS = 60_000;
/**
 * "Just this session": a session endorsement lasts at most 12 h. Verifiers enforce this cap for
 * any statement with `scope: 'session'`, whatever TTL the verifier otherwise allows.
 */
export const SESSION_ENDORSEMENT_MAX_TTL_MS = 12 * 60 * 60 * 1000;
export async function createDeviceEndorsementStatement(input) {
    const jwk = isEcPublicJwk(input.publicKey)
        ? { kty: 'EC', crv: 'P-256', x: input.publicKey.x, y: input.publicKey.y }
        : await exportP256PublicJwk(input.publicKey);
    const issuedAt = input.now ?? Date.now();
    const ttlMs = input.session
        ? Math.min(input.ttlMs ?? SESSION_ENDORSEMENT_MAX_TTL_MS, SESSION_ENDORSEMENT_MAX_TTL_MS)
        : (input.ttlMs ?? DEFAULT_ENDORSEMENT_TTL_MS);
    return {
        v: 1,
        type: 'viko.device-endorsement',
        deviceKeyId: await jwkThumbprint(jwk),
        publicKey: jwk,
        label: input.label,
        issuedAt,
        expiresAt: issuedAt + ttlMs,
        ...(input.session ? { scope: 'session' } : {}),
    };
}
/** True for a "Just this session" endorsement statement. */
export function isSessionEndorsement(statement) {
    return statement?.scope === 'session';
}
/** The WebAuthn challenge for the endorsement tap: SHA-256 over the domain-tagged JCS statement. */
export async function createDeviceEndorsementChallenge(statement) {
    return hashCanonical(DOMAIN.deviceEndorsement, statement);
}
function isStatement(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const s = v;
    return (s.v === 1 &&
        s.type === 'viko.device-endorsement' &&
        typeof s.deviceKeyId === 'string' &&
        isEcPublicJwk(s.publicKey) &&
        typeof s.label === 'string' &&
        Number.isSafeInteger(s.issuedAt) &&
        Number.isSafeInteger(s.expiresAt) &&
        (s.scope === undefined || s.scope === 'session'));
}
/**
 * Offline check that a device key was endorsed by a pinned passkey: statement shape, key id,
 * validity window, revocation, then the WebAuthn assertion over the statement hash.
 */
export async function verifyDeviceEndorsement(endorsement, options) {
    const statement = endorsement?.statement;
    if (!isStatement(statement) || !isWebAuthnAssertion(endorsement.assertion)) {
        return { ok: false, reason: 'malformed' };
    }
    const now = options.now ?? Date.now();
    const skew = options.clockSkewMs ?? DEFAULT_CLOCK_SKEW_MS;
    const publicJwk = {
        kty: 'EC',
        crv: 'P-256',
        x: statement.publicKey.x,
        y: statement.publicKey.y,
    };
    if ((await jwkThumbprint(publicJwk)) !== statement.deviceKeyId) {
        return { ok: false, reason: 'key-mismatch' };
    }
    if (options.revokedDevices?.has(statement.deviceKeyId)) {
        return { ok: false, reason: 'revoked-device' };
    }
    if (statement.issuedAt > now + skew)
        return { ok: false, reason: 'not-yet-valid' };
    if (now > statement.expiresAt + skew)
        return { ok: false, reason: 'expired' };
    const session = isSessionEndorsement(statement);
    const maxTtlMs = options.maxTtlMs ?? DEFAULT_ENDORSEMENT_TTL_MS;
    if (statement.expiresAt - statement.issuedAt >
        (session ? Math.min(maxTtlMs, SESSION_ENDORSEMENT_MAX_TTL_MS) : maxTtlMs)) {
        return { ok: false, reason: 'ttl-too-long' };
    }
    let publicKey;
    try {
        publicKey = await importP256PublicJwk(publicJwk);
    }
    catch {
        return { ok: false, reason: 'malformed', detail: 'invalid device public key' };
    }
    let challenge;
    try {
        challenge = await approvalChallengeFor(DOMAIN.deviceEndorsement, await createDeviceEndorsementChallenge(statement), endorsement.batch);
    }
    catch (e) {
        return { ok: false, reason: 'malformed', detail: e.message };
    }
    const approval = await verifyPasskeyApproval(endorsement.assertion, challenge, {
        credentials: options.credentials,
        rp: options.rp,
        ...(options.revokedCredentials ? { revokedCredentials: options.revokedCredentials } : {}),
    });
    if (!approval.ok)
        return approval;
    return {
        ok: true,
        device: {
            deviceKeyId: statement.deviceKeyId,
            publicJwk,
            publicKey,
            label: statement.label,
            endorsedBy: approval.credential.credentialId,
            issuedAt: statement.issuedAt,
            expiresAt: statement.expiresAt,
            session,
            endorsement,
        },
    };
}
//# sourceMappingURL=endorsement.js.map